import React, { useState, useEffect } from 'react';
import axios from 'axios';
import config from '../config';

const UserForm = ({ onSubmit, initialData, onViewFolders }) => {
  const [name, setName] = useState(initialData?.name || '');
  const [printSize, setPrintSize] = useState('');
  const [quantity, setQuantity] = useState('');
  const [sizes, setSizes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchSizes();
  }, []);

  const fetchSizes = async () => {
    try {
      // Get product type from URL parameter (default to PHOTO)
      const urlParams = new URLSearchParams(window.location.search);
      const productParam = urlParams.get('product'); // e.g., "METAL"
      const productType = productParam ? productParam.toUpperCase() : 'PHOTO';
      
      // Fetch sizes filtered by product type
      const response = await axios.get(`${config.apiUrl}/sizes`, {
        params: { productType }
      });
      
      const sizesData = response.data.sizes || [];
      setSizes(sizesData);
      
      // Set first size as default
      if (sizesData.length > 0) {
        setPrintSize(sizesData[0].id.toString());
      }
      
      setLoading(false);
    } catch (err) {
      console.error('Error fetching sizes:', err);
      setError('Failed to load print sizes');
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!name.trim()) {
      setError('Please enter your name');
      return;
    }
    
    if (!printSize) {
      setError('Please select a print size');
      return;
    }
    
    if (!quantity || parseInt(quantity) < 1) {
      setError('Please enter a valid quantity');
      return;
    }

    setError('');
    onSubmit({
      name: name.trim(),
      sizeId: parseInt(printSize),
      quantity: parseInt(quantity),
    });
  };

  return (
    <div className="w-full max-w-md mx-auto p-6 bg-white rounded-xl shadow-lg">
      <div className="text-center mb-6">
        <h1 className="text-3xl font-bold text-gray-800 mb-2 flex items-center justify-center gap-2">
          <img src="/logo/logo.png" alt="DripPrints" className="w-10 h-10 object-contain" />
          DripPrints
        </h1>
        <div className="text-gray-600 space-y-1">
          <p>❤️ Keep your moments ❤️</p>
          <p>📸 Keep the same quality 📸</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Name Input */}
        <div>
          <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
            Your Name
          </label>
          <input
            type="text"
            id="name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none transition-all"
            placeholder="Enter your name"
            required
          />
        </div>

        {/* Print Size Dropdown */}
        <div>
          <label htmlFor="printSize" className="block text-sm font-medium text-gray-700 mb-2">
            Print Size
          </label>
          {loading ? (
            <div className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-gray-50 text-gray-400">
              Loading sizes...
            </div>
          ) : sizes.length === 0 ? (
            <div className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-gray-50 text-gray-400">
              No sizes available for this product type
            </div>
          ) : (
            <select
              id="printSize"
              value={printSize}
              onChange={(e) => setPrintSize(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none transition-all appearance-none bg-white"
              required
            >
              {sizes.map((size) => (
                <option key={size.id} value={size.id}>
                  {size.label}
                </option>
              ))}
            </select>
          )}
        </div>

        {/* Quantity Input */}
        <div>
          <label htmlFor="quantity" className="block text-sm font-medium text-gray-700 mb-2">
            Quantity
          </label>
          <input
            type="number"
            id="quantity"
            value={quantity}
            onChange={(e) => setQuantity(e.target.value)}
            min="1"
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none transition-all"
            placeholder="Enter quantity"
            required
          />
        </div>

        {/* Error Message */}
        {error && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
            {error}
          </div>
        )}

        {/* Submit Button */}
        <button
          type="submit"
          className="w-full bg-primary-600 hover:bg-primary-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors duration-200 shadow-md hover:shadow-lg active:scale-95 transform"
        >
          Continue to Upload
        </button>

        {/* View Your Folders Button */}
        {onViewFolders && (
          <button
            type="button"
            onClick={onViewFolders}
            className="w-full bg-primary-600 hover:bg-primary-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors duration-200 shadow-md hover:shadow-lg active:scale-95 transform"
          >
            📁 View folders
          </button>
        )}
      </form>
    </div>
  );
};

export default UserForm;

